/**
 * ResetChildPasswordDialog component
 * Accessible modal form for resetting a child's password from the parent dashboard.
 *
 * Implements US-002 (T-004).
 *
 * Overlay shell copied from RenameChildDialog.tsx ~60% verbatim:
 *   (role=dialog, aria-modal, fixed inset-0 z-50, Escape/backdrop close guarded
 *   by isPending, reset-on-open effect, close-on-success effect, error block).
 * Password field + validatePassword + <PasswordRequirements> + submit-block-on-invalid
 *   copied from AddChildDialog.tsx:122-127,196-218 (~10% of component).
 * Net-new logic: NETWORK_ERROR message mapping in the error-display block.
 *
 * TO-LL-036 enforcement: errors are surfaced inline; dialog stays open on every error
 *   (closes ONLY on isSuccess). No try/catch in submit handler — errors propagate from
 *   the mutation layer (resetChildPassword service never catches). The password field
 *   value is never cleared on error so the parent can retry without retyping (AC-6).
 * TO-LL-044 enforcement: childId passed to mutate as exactly `childId` (camelCase),
 *   body key sent to the API is exactly `password` (enforced by ResetChildPasswordVariables).
 * Security: password sent only in request body; never logged, echoed, or placed in URL.
 *   type='password' + autoComplete='new-password' mask the value in the browser.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { validatePassword } from '@/lib/passwordValidator';
import { useChildMutations } from '@/hooks/useChildMutations';
import type { ChildSummary } from '@/types/coins';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ResetChildPasswordDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /**
   * The child whose password will be reset. Must be non-null when isOpen is true.
   * Dialog renders null when child is null — safe if grid refetch removes the child.
   */
  child: ChildSummary | null;
  /** Callback to close the dialog */
  onClose: () => void;
}

// ─── ResetChildPasswordDialog (main export) ───────────────────────────────────

/**
 * ResetChildPasswordDialog — overlay modal for resetting a child's password.
 *
 * Single password field with live <PasswordRequirements> checklist.
 * Sends POST /children/{childId}/reset-password with body { password } ONLY.
 * userId, username, displayName, and email remain immutable (AC-3/AC-5).
 *
 * States:
 *  - Idle: field editable, Reset active when password is non-empty
 *  - Submitting (resetPassword.isPending): buttons disabled, "Resetting…" label
 *  - Error (resetPassword.error or clientError): inline red alert, dialog stays open (TO-LL-036)
 *  - Success (resetPassword.isSuccess): dialog closes; dashboard refetches via ['parent','dashboard']
 *
 * Implements US-002; enforces TO-LL-036 (inline errors, onSuccess-only close)
 * and TO-LL-044 (camelCase body key `password`).
 */
export function ResetChildPasswordDialog({ isOpen, child, onClose }: ResetChildPasswordDialogProps) {
  const { resetPassword } = useChildMutations();

  // ─── Form state ───────────────────────────────────────────────────────────
  const [password, setPassword] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);

  // Focus target: password input
  const inputRef = useRef<HTMLInputElement>(null);

  // ─── Reset state when dialog opens or child changes ──────────────────────
  // Mirror of RenameChildDialog:72-88. Password field is cleared on open
  // (unlike RenameChildDialog which pre-fills the current name).
  // DO NOT select() a password field — selecting masked characters is not useful.
  useEffect(() => {
    if (isOpen && child) {
      setPassword('');
      setClientError(null);
      resetPassword.reset();
      // Focus the password input after DOM is ready
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, child]);

  // ─── Close on success (AC-3) ──────────────────────────────────────────────
  // Mirror of RenameChildDialog:91-95. Dialog closes ONLY on isSuccess — never on error.
  useEffect(() => {
    if (resetPassword.isSuccess) {
      onClose();
    }
  }, [resetPassword.isSuccess, onClose]);

  // ─── Keyboard handler: Escape closes ─────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && !resetPassword.isPending) {
        onClose();
      }
    },
    [onClose, resetPassword.isPending]
  );

  // ─── Form submission ──────────────────────────────────────────────────────
  // Implements US-002 (T-004). Enforces TO-LL-036 (block before API on invalid input).
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!child) return;
      setClientError(null);

      // Client-side policy check — blocks API call on weak password (AC-4 first half).
      // TO-LL-036: surface the failure clearly; PasswordRequirements shows which rule failed.
      const { isValid } = validatePassword(password);
      if (!isValid) {
        setClientError('Password does not meet the requirements below.');
        return;
      }

      // TO-LL-044: body key is exactly `password` (enforced by ResetChildPasswordVariables type).
      // TO-LL-036: errors propagate from resetChildPassword service; no try/catch here.
      // Security: password is never logged; sent only in the POST body.
      resetPassword.mutate({ childId: child.userId, password });
    },
    [child, password, resetPassword]
  );

  // Render nothing when not open or child not provided
  if (!isOpen || !child) return null;

  // ─── Error message selection ──────────────────────────────────────────────
  // TO-LL-036: errors surfaced inline; dialog stays open on every non-success state.
  // NETWORK_ERROR (code set by api.ts:52-56) is mapped to a retry-inviting message (AC-6).
  // Server 400/403/500 messages are shown verbatim from the AuthError (AC-4/AC-5).
  // clientError (weak-password) is the fallback when no server error is present.
  // The password field is NOT cleared on error — state survives so the parent can retry (AC-6).
  const serverError = resetPassword.error;
  const errorMessage =
    serverError?.code === 'NETWORK_ERROR'
      ? 'Unable to reach the server. Please check your connection and try again.'
      : (serverError?.message ?? clientError);

  const isSubmitDisabled = resetPassword.isPending || password.trim() === '';

  return (
    /* Overlay — same pattern as RenameChildDialog / DemoLimitPopup (role=dialog, aria-modal, fixed inset-0 z-50) */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-child-password-dialog-title"
      onKeyDown={handleKeyDown}
      onClick={(e) => {
        // Close on backdrop click (only if not submitting) — mirror of RenameChildDialog
        if (e.target === e.currentTarget && !resetPassword.isPending) {
          onClose();
        }
      }}
    >
      {/* Dialog card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 w-full max-w-sm mx-4">
        {/* Title */}
        <h2
          id="reset-child-password-dialog-title"
          className="text-lg md:text-xl font-bold text-gray-900 mb-1"
        >
          Reset {child.displayName}&apos;s password
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          Set a new password for {child.displayName}. Their username does not change.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Password field — type='password', autoComplete='new-password' (AC-5 security) */}
          <div className="space-y-1">
            <Label htmlFor="reset-child-password" className="text-sm font-medium text-gray-700">
              New password <span aria-hidden="true" className="text-red-500">*</span>
            </Label>
            <PasswordInput
              id="reset-child-password"
              ref={inputRef}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={resetPassword.isPending}
              autoComplete="new-password"
              className="h-11"
            />
            {/* Live policy checklist — shown when user starts typing (AC-2) */}
            {/* Reused from AddChildDialog.tsx:213-217 */}
            {password.length > 0 && (
              <div className="pt-1">
                <PasswordRequirements password={password} />
              </div>
            )}
          </div>

          {/* Inline error message — surfaced, never swallowed (TO-LL-036) */}
          {/* Dialog stays open on every error (AC-4/AC-5/AC-6). */}
          {errorMessage && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
            >
              {errorMessage}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={isSubmitDisabled}
              className="flex-1 h-11"
            >
              {resetPassword.isPending ? 'Resetting…' : 'Reset password'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={resetPassword.isPending}
              onClick={onClose}
              className="flex-1 h-11"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
